import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { get } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen } from "../../../components/admin/EssUi";
import { Search, Upload, Folder, FileText, ScrollText, Receipt } from "lucide-react";

const GREEN = "#225f2b";
const BORDER = "#e2e7e2";

const ROWS_PER_PAGE = 10;

const mapModelToType = (resModel) => {
  if (!resModel || resModel === false) return "File";
  const map = {
    "hr.expense": "Expense Receipt",
    "hr.expense.sheet": "Expense Report",
    "hr.employee": "Employee Document",
    "hr.contract": "Contract",
    "hr.leave": "Leave Attachment",
    "hr.applicant": "Application",
    "hr.job": "Job Description",
    "hr.payslip": "Payslip",
    "hr.attendance": "Attendance Report",
    "hr.training": "Training Material",
  };
  return map[resModel] || resModel.replace(/^hr\./, "").replace(/_/g, " ");
};

const statusChip = (status) => {
  if (status === "Active") return "ess-chip ess-chip-green";
  if (status === "Expired") return "ess-chip ess-chip-red";
  if (status === "Pending Signature") return "ess-chip ess-chip-orange";
  return "ess-chip ess-chip-gray";
};

const AdminPanelDocumentsPage = () => {
  const intl = useIntl();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const r = await get("/api/documents");
        const docs = (r && r.data && Array.isArray(r.data)) ? r.data : (Array.isArray(r) ? r : []);
        setData(docs.map((e, i) => {
          const expiry = e.expiry_date || e.expiry || "—";
          const isExpired = expiry !== "—" && expiry !== "Permanent" && new Date(expiry) < new Date();
          return {
            id: e.id || i,
            name: e.name || "—",
            type: mapModelToType(e.res_model),
            employee: e.employee_name || "—",
            issuedBy: e.issued_by || "—",
            issueDate: e.create_date
              ? new Date(e.create_date.split(" ")[0]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : "—",
            expiry: expiry,
            status: isExpired ? "Expired" : (e.state || "Active"),
            fileSize: e.file_size,
            mimetype: e.mimetype,
          };
        }));
      } catch { setData([]); }
      setLoaded(true);
    })();
  }, []);

  if (!loaded) return <LoadingScreen />;

  const docTypes = [...new Set(data.map(r => r.type))];
  const statuses = [...new Set(data.map(r => r.status))];

  const getTabFilter = () => {
    if (activeTab === "contracts") return { key: "type", values: ["Employment Contract", "NDA", "Offer Letter", "Contract"] };
    if (activeTab === "payslips") return { key: "type", values: ["Payslip"] };
    if (activeTab === "expenses") return { key: "type", values: ["Expense Receipt", "Expense Report"] };
    return null;
  };
  const tabFilter = getTabFilter();

  const filtered = data.filter(r => {
    if (tabFilter && !tabFilter.values.includes(r.type)) return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (typeFilter !== "all" && r.type !== typeFilter) return false;
    if (search.trim() && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const paginatedData = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const contractsCount = data.filter(r => ["Employment Contract", "NDA", "Offer Letter", "Contract"].includes(r.type)).length;
  const payslipsCount = data.filter(r => r.type === "Payslip").length;
  const expensesCount = data.filter(r => ["Expense Receipt", "Expense Report"].includes(r.type)).length;

  const tabs = [
    { key: "all", label: "All Documents", count: data.length },
    { key: "contracts", label: "Contracts & Legal", count: contractsCount },
    { key: "payslips", label: "Payslips", count: payslipsCount },
    { key: "expenses", label: "Expenses", count: expensesCount },
  ];

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: '0 16px' }}>
      <PageHeader
        title="Documents"
        subtitle="Manage HR documents, contracts and employee files"
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="ess-btn ess-btn-primary">
              <Upload size={15} />
              Upload Document
            </button>
            <button className="ess-btn ess-btn-secondary">
              <Folder size={15} />
              New Folder
            </button>
          </div>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: '20px' }}>
        <StatCard label="Total Documents" value={data.length} icon={FileText} />
        <StatCard label="Contracts & Legal" value={contractsCount} icon={ScrollText} />
        <StatCard label="Expense Receipts" value={expensesCount} icon={Receipt} />
        <StatCard label="Other Files" value={data.length - contractsCount - payslipsCount - expensesCount} icon={Folder} />
      </div>

      <div style={{ display: 'flex', gap: '24px', marginBottom: '20px', borderBottom: `1px solid ${BORDER}`, overflowX: 'auto' }}>
        {tabs.map((tab) => (
          <button key={tab.key} onClick={() => { setActiveTab(tab.key); setPage(1); }}
            style={{
              padding: '9px 2px', border: 'none', background: 'transparent',
              borderBottom: `2px solid ${activeTab === tab.key ? GREEN : "transparent"}`,
              marginBottom: -1, whiteSpace: 'nowrap',
              color: activeTab === tab.key ? GREEN : '#64748b', fontSize: '13px',
              fontWeight: activeTab === tab.key ? '600' : '500', cursor: 'pointer', transition: 'color 0.15s',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{ background: activeTab === tab.key ? '#f0f7f0' : '#f1f5f9', color: activeTab === tab.key ? GREEN : '#64748b', padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="ess-fbar" style={{ padding: 16, marginBottom: '20px', background: 'white', borderRadius: '8px', border: `1px solid ${BORDER}` }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search documents..."
            style={{ width: '100%', padding: '9px 12px 9px 36px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13.5px', outline: 'none', background: '#fff' }}
          />
        </div>
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          style={{ padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13.5px', background: '#fff', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Types</option>
          {docTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13.5px', background: '#fff', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Status</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: 'white', borderRadius: '8px', border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ess-tbl" style={{ minWidth: '760px' }}>
            <thead>
              <tr>
                <th className="ess-th">Document</th>
                <th className="ess-th">Type</th>
                <th className="ess-th">Issue Date</th>
                <th className="ess-th">Expiry</th>
                <th className="ess-th">Status</th>
                <th className="ess-th ess-th-r"></th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr><td colSpan={6}><div className="ess-empty">No documents found.</div></td></tr>
              ) : paginatedData.map((row) => (
                <tr key={row.id} className="ess-tr">
                  <td className="ess-td" style={{ fontSize: '13.5px', fontWeight: 600, color: '#1e293b', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</td>
                  <td className="ess-td"><span className="ess-chip ess-chip-gray">{row.type}</span></td>
                  <td className="ess-td" style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{row.issueDate}</td>
                  <td className="ess-td" style={{ fontWeight: row.expiry !== "—" && row.expiry !== "Permanent" ? 600 : 400, color: row.expiry !== "—" && row.expiry !== "Permanent" ? '#c72d33' : '#94a3b8', whiteSpace: 'nowrap' }}>{row.expiry}</td>
                  <td className="ess-td"><span className={statusChip(row.status)}>{row.status}</span></td>
                  <td className="ess-td ess-th-r">
                    <button style={{ border: 'none', background: 'transparent', color: GREEN, fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                      onMouseEnter={(e) => e.target.style.background = '#f0f7f0'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="ess-btn ess-btn-secondary">← Previous</button>
          <span style={{ fontSize: '13px', color: '#64748b' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="ess-btn ess-btn-secondary">Next →</button>
        </div>
      )}
    </div>
  );
};

export default AdminPanelDocumentsPage;